/**
 * Canonical LumaSkill Data Structures
 *
 * Single source of truth for Luma Skill representation in the frontend.
 * These interfaces define how we store and manipulate Luma Skills internally,
 * independent of the OOTB format from the Python generator.
 */

/**
 * Node types supported by the generator
 * Matches the 16 validated node types
 */
export enum NodeType {
  START = 'START',
  END = 'END',
  MESSAGE = 'MESSAGE',
  PROMPT_ATTRIBUTE = 'PROMPT_ATTRIBUTE',
  DECISION_PROMPT = 'DECISION_PROMPT',
  SET_ATTRIBUTE = 'SET_ATTRIBUTE',
  CONDITION = 'CONDITION',
  INTEGRATION = 'INTEGRATION',
  AI_TASK = 'AI_TASK',
  SWITCH_SKILL = 'SWITCH_SKILL',
  EXECUTE_SUBFLOW = 'EXECUTE_SUBFLOW',
  CLEAR_ATTRIBUTE = 'CLEAR_ATTRIBUTE',
  SEARCH_SKILLS = 'SEARCH_SKILLS',
  DISPLAY_SKILL = 'DISPLAY_SKILL',
  EXECUTE_SCRIPT = 'EXECUTE_SCRIPT',
  SUGGEST_SKILLS = 'SUGGEST_SKILLS',
  SEARCH_KNOWLEDGE = 'SEARCH_KNOWLEDGE',
  DISPLAY_KNOWLEDGE = 'DISPLAY_KNOWLEDGE'
}

/**
 * Position of a node on the canvas
 */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * A node in the Luma Skill
 */
export interface LumaSkillNode {
  id: string;                   // Unique node ID (e.g., "start_1")
  type: NodeType;               // Node type
  name: string;                 // Display name
  position: NodePosition;       // Canvas position
  properties: Record<string, any>;  // Type-specific properties
}

/**
 * A link (connection) between nodes
 */
export interface LumaSkillLink {
  id: string;                   // Unique link ID
  source: string;               // Source node ID
  target: string;               // Target node ID
  sourcePort?: string;          // Source port ID (optional)
  targetPort?: string;          // Target port ID (optional)
  label?: string;               // Edge label (for conditions, etc.)
}

/**
 * Validation issue from Python validator
 */
export interface ValidationIssue {
  rule: string;                 // Rule name
  severity: 'error' | 'warning';
  message: string;
  location?: string;            // Optional location hint
}

/**
 * Validation result from Python validator
 */
export interface ValidationResult {
  confidence: number;           // 0-100
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  passed: string[];             // Names of passed rules
}

/**
 * Complete Luma Skill representation
 * This is our canonical format for storing and manipulating skills
 */
export interface LumaSkill {
  identifier: string;           // e.g., "test_v34_111D"
  name: string;                 // Display name
  description: string;          // Skill description
  nodes: LumaSkillNode[];       // All nodes in the skill
  links: LumaSkillLink[];       // All connections between nodes
  validation?: ValidationResult; // Optional validation results
}

/**
 * OOTB Luma cell types
 * These match the "type" field in designerFlowDefinition.cells
 */
export const OOTB_NODE_TYPE_MAP: Record<string, NodeType> = {
  // Standard OOTB format (luma.*)
  'luma.Start': NodeType.START,
  'luma.End': NodeType.END,
  'luma.Message': NodeType.MESSAGE,
  'luma.PromptAttribute': NodeType.PROMPT_ATTRIBUTE,
  'luma.DecisionPromptAttribute': NodeType.DECISION_PROMPT,
  'luma.SetAttribute': NodeType.SET_ATTRIBUTE,
  'luma.Condition': NodeType.CONDITION,
  'luma.Integration': NodeType.INTEGRATION,
  'luma.AITask': NodeType.AI_TASK,
  'luma.SwitchSkill': NodeType.SWITCH_SKILL,
  'luma.ExecuteSubFlow': NodeType.EXECUTE_SUBFLOW,
  'luma.ClearAttribute': NodeType.CLEAR_ATTRIBUTE,
  'luma.SearchSkills': NodeType.SEARCH_SKILLS,
  'luma.DisplaySkill': NodeType.DISPLAY_SKILL,
  'luma.ExecuteScript': NodeType.EXECUTE_SCRIPT,
  'luma.SuggestSkills': NodeType.SUGGEST_SKILLS,
  'luma.SearchKnowledge': NodeType.SEARCH_KNOWLEDGE,
  'luma.DisplayKnowledge': NodeType.DISPLAY_KNOWLEDGE,
  'SearchKnowledge': NodeType.SEARCH_KNOWLEDGE,
  'DisplayKnowledge': NodeType.DISPLAY_KNOWLEDGE,
  'SEARCH': NodeType.SEARCH_KNOWLEDGE,
  'DISPLAY': NodeType.DISPLAY_KNOWLEDGE,
  'CELL_TYPE': NodeType.MESSAGE,

  // Alternative format from Python transformer (NodeType.*)
  // TODO: Fix Python transformer to output correct luma.* format
  'NodeType.START': NodeType.START,
  'NodeType.END': NodeType.END,
  'NodeType.MESSAGE': NodeType.MESSAGE,
  'NodeType.PROMPT_ATTRIBUTE': NodeType.PROMPT_ATTRIBUTE,
  'NodeType.DECISION_PROMPT': NodeType.DECISION_PROMPT,
  'NodeType.SET_ATTRIBUTE': NodeType.SET_ATTRIBUTE,
  'NodeType.CONDITION': NodeType.CONDITION,
  'NodeType.INTEGRATION': NodeType.INTEGRATION,
  'NodeType.AI_TASK': NodeType.AI_TASK,
  'NodeType.SWITCH_SKILL': NodeType.SWITCH_SKILL,
  'NodeType.EXECUTE_SUBFLOW': NodeType.EXECUTE_SUBFLOW,
  'NodeType.CLEAR_ATTRIBUTE': NodeType.CLEAR_ATTRIBUTE,
  'NodeType.SEARCH_SKILLS': NodeType.SEARCH_SKILLS,
  'NodeType.DISPLAY_SKILL': NodeType.DISPLAY_SKILL,
  'NodeType.EXECUTE_SCRIPT': NodeType.EXECUTE_SCRIPT,
  'NodeType.SUGGEST_SKILLS': NodeType.SUGGEST_SKILLS,
  'NodeType.SEARCH_KNOWLEDGE': NodeType.SEARCH_KNOWLEDGE,
  'NodeType.DISPLAY_KNOWLEDGE': NodeType.DISPLAY_KNOWLEDGE,

  // New format cellTypes (bare strings)
  'START': NodeType.START,
  'END': NodeType.END,
  'MESSAGE': NodeType.MESSAGE,
  'PROMPT': NodeType.PROMPT_ATTRIBUTE,
  'DECISION_PROMPT': NodeType.DECISION_PROMPT,
  'SET_ATTRIBUTE': NodeType.SET_ATTRIBUTE,
  'CONDITION': NodeType.CONDITION,
  'INTEGRATION': NodeType.INTEGRATION,
  'AI_TASK': NodeType.AI_TASK,
  'SWITCH_SKILL': NodeType.SWITCH_SKILL,
  'EXECUTE_SUBFLOW': NodeType.EXECUTE_SUBFLOW,
  'CLEAR_ATTRIBUTE': NodeType.CLEAR_ATTRIBUTE,
  'SEARCH_SKILLS': NodeType.SEARCH_SKILLS,
  'DISPLAY_SKILL': NodeType.DISPLAY_SKILL,
  'EXECUTE_SCRIPT': NodeType.EXECUTE_SCRIPT,
  'SUGGEST_SKILLS': NodeType.SUGGEST_SKILLS,
  'SEARCH_KNOWLEDGE': NodeType.SEARCH_KNOWLEDGE,
  'DISPLAY_KNOWLEDGE': NodeType.DISPLAY_KNOWLEDGE
};

/**
 * Reverse map: NodeType → OOTB type string
 */
export const NODE_TYPE_TO_OOTB: Record<NodeType, string> = Object.entries(OOTB_NODE_TYPE_MAP).reduce(
  (acc, [ootbType, nodeType]) => {
    acc[nodeType] = ootbType;
    return acc;
  },
  {} as Record<NodeType, string>
);

/**
 * Check if a node type is terminal (never returns control to caller)
 */
export function isTerminalNode(type: NodeType): boolean {
  return type === NodeType.END || type === NodeType.SWITCH_SKILL;
}

/**
 * Check if a node type has implicit input port (no port field in link target)
 */
export function hasImplicitInputPort(type: NodeType): boolean {
  return (
    type === NodeType.EXECUTE_SUBFLOW ||
    type === NodeType.CLEAR_ATTRIBUTE ||
    type === NodeType.SEARCH_SKILLS
  );
}

/**
 * Get default color for node type (for visual representation)
 */
export function getNodeColor(type: NodeType): string {
  const colorMap: Record<NodeType, string> = {
    [NodeType.START]: '#10b981',           // green-500
    [NodeType.END]: '#ef4444',             // red-500
    [NodeType.MESSAGE]: '#3b82f6',         // blue-500
    [NodeType.PROMPT_ATTRIBUTE]: '#a855f7', // purple-500
    [NodeType.DECISION_PROMPT]: '#eab308',  // yellow-500
    [NodeType.SET_ATTRIBUTE]: '#f97316',    // orange-500
    [NodeType.CONDITION]: '#eab308',        // yellow-500
    [NodeType.INTEGRATION]: '#14b8a6',      // teal-500
    [NodeType.AI_TASK]: '#ec4899',          // pink-500
    [NodeType.SWITCH_SKILL]: '#ef4444',     // red-500
    [NodeType.EXECUTE_SUBFLOW]: '#3b82f6',  // blue-500
    [NodeType.CLEAR_ATTRIBUTE]: '#6b7280',  // gray-500
    [NodeType.SEARCH_SKILLS]: '#10b981',    // green-500
    [NodeType.DISPLAY_SKILL]: '#06b6d4',    // cyan-500
    [NodeType.EXECUTE_SCRIPT]: '#8b5cf6',   // violet-500
    [NodeType.SUGGEST_SKILLS]: '#10b981',   // green-500
    [NodeType.SEARCH_KNOWLEDGE]: '#0ea5e9', // sky-500
    [NodeType.DISPLAY_KNOWLEDGE]: '#6366f1' // indigo-500
  };
  return colorMap[type] || '#6b7280'; // default to gray
}

/**
 * Get icon name for node type (lucide-react icon name)
 */
export function getNodeIcon(type: NodeType): string {
  const iconMap: Record<NodeType, string> = {
    [NodeType.START]: 'Play',
    [NodeType.END]: 'Square',
    [NodeType.MESSAGE]: 'MessageSquare',
    [NodeType.PROMPT_ATTRIBUTE]: 'MessageCircle',
    [NodeType.DECISION_PROMPT]: 'GitBranch',
    [NodeType.SET_ATTRIBUTE]: 'Settings',
    [NodeType.CONDITION]: 'GitBranch',
    [NodeType.INTEGRATION]: 'Plug',
    [NodeType.AI_TASK]: 'Sparkles',
    [NodeType.SWITCH_SKILL]: 'ArrowRightLeft',
    [NodeType.EXECUTE_SUBFLOW]: 'FunctionSquare',
    [NodeType.CLEAR_ATTRIBUTE]: 'Eraser',
    [NodeType.SEARCH_SKILLS]: 'Search',
    [NodeType.DISPLAY_SKILL]: 'Eye',
    [NodeType.EXECUTE_SCRIPT]: 'Code',
    [NodeType.SUGGEST_SKILLS]: 'Lightbulb',
    [NodeType.SEARCH_KNOWLEDGE]: 'FileSearch',
    [NodeType.DISPLAY_KNOWLEDGE]: 'BookOpen'
  };
  return iconMap[type] || 'Circle';
}
